import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import CustomModal from "../../packages/CustomModal";
import WPAgGrid from "../../packages/WPAgGrid/WPAgGrid";
import WPButton from "../../packages/WPButton";
import WPPagination from "../../packages/WPPagination";
import { useToastNotification } from "../../packages/WPToastNotification/WPToastNotificationProvider";

import RequestStatusFilterHeader from "../../components/AdminPortal/customFilter";
import PlantsCountRenderer from "../../components/AdminPortal/plantsCountRenderer";
import ViewPlantCellRenderer from "../../components/AdminPortal/ViewPlantCellRenderer";
import Header from "../../components/Header";

import {
  DEFAULT_PAGE_SIZE_OPTION,
  ViewTypeConstants,
} from "../../constants/global.constants";

import Footer from "../../common/footer/Footer";
import CircularLoader from "../../common/styles/components/circularLoader";

import { UserExcelStrings } from "../../utils/constantsString";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "../../utils/session";
import { dateFormatter } from "../../utils/tableDataFormatter";
import {
  downloadBlobData,
  emptyValueFormatter,
  formatToLocalDateTime,
  getRequestStatusText,
  updatePlantId,
} from "../../utils/Utils";

import { API_URLS } from "../../network/appUrls";
import {
  exportExcel,
  getAllPlantsForUser,
  getAllUsers,
} from "../../network/cipAPI";

import { storeAccessUserPlant } from "../../Store/TemplateStore/template.action";

import "./index.scss";

const USER_MANAGEMENT_SELECTED_USER = "userManagementSelectedUser";

const UserPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state);
  const { showSuccessNotification, showErrorNotification } =
    useToastNotification();

  const [showOverLayLoader, setShowOverLayLoader] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE_OPTION);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [plantRowData, setPlantRowData] = useState([]);
  const [selectedUser, setSelectedUser] = useState({ name: "", email: "" });
  const [exportLoading, setExportLoading] = useState(false);

  // Row click handler for plant dialog table
  const handleViewPlant = (event) => {
    const { data } = event;

    // Persist current modal user context so we can reopen it after returning
    // from plant pages via "Back to User Management".
    if (selectedUser?.email) {
      setLocalStorageItem(
        USER_MANAGEMENT_SELECTED_USER,
        JSON.stringify({
          name: selectedUser.name || "",
          email: selectedUser.email,
        }),
      );
    }

    if (data.view === ViewTypeConstants.VIEW_EDIT_PLANT) {
      updatePlantId(data.plant_id);
      dispatch(storeAccessUserPlant(true));
      const path = `${API_URLS.basePath}editPlant?plantId=${data.plant_id}&isEdit=true`;
      navigate(path, { state: { noOfTrains: data.num_trains } });
    } else if (data.view === ViewTypeConstants.VIEW_ANALYSE_DATA) {
      updatePlantId(data.plant_id);
      dispatch(storeAccessUserPlant(true));
      const path = `${API_URLS.basePath}analyseData?plantId=${data.plant_id}&isEdit=true`;
      navigate(path);
    } else if (data.view === ViewTypeConstants.VIEW_HOME) {
      updatePlantId(data.plant_id);
      dispatch(storeAccessUserPlant(true));
      const path = `${API_URLS.basePath}home?plantId=${data.plant_id}`;
      navigate(path);
    }
  };

  // Handler to open dialog from PlantsCountRenderer
  const handleShowPlants = async ({ data }) => {
    setDialogOpen(true);
    setShowOverLayLoader(true);
    const userName = data?.username || data?.name || ""; // to handle both viewPlant & back to user management scenarios where the field name differ
    const userEmail = data?.email || "";
    setSelectedUser({ name: userName, email: userEmail });
    try {
      let plants = [];
      if (data && data.email) {
        const response = await getAllPlantsForUser(userEmail);
        if (response && response.data) {
          plants = response.data;
        }
      }
      setPlantRowData(plants);
    } catch (err) {
      setPlantRowData([]);
      setDialogOpen(true);
    } finally {
      setShowOverLayLoader(false);
    }
  };

  // Plant table column definitions for dialog
  const plantColDefs = [
    {
      headerName: t("ROOperationAdvisorTableStrings.name"),
      field: "name",
      sortable: true,
      flex: 1,
      wrapText: true,
      minWidth: 180,
    },

    {
      headerName: t("ROOperationAdvisorTableStrings.city"),
      field: "city",
      sortable: true,
      flex: 1,
      minWidth: 100,
    },
    {
      headerName: t("ROOperationAdvisorTableStrings.country"),
      field: "country",
      sortable: true,
      flex: 1,
      wrapText: true,
      minWidth: 150,
    },
    {
      headerName: t("ROOperationAdvisorTableStrings.createdon"),
      field: "created_at",
      valueFormatter: dateFormatter,
      sortable: true,
      flex: 1,
      minWidth: 150,
      sort: "desc",
    },
    {
      colId: "viewPlant",
      lockPinned: true,
      headerName: "View Plant",
      field: "viewPlant",
      pinned: "right",
      width: 150,
      cellRenderer: ViewPlantCellRenderer,
    },
  ];

  const handlePlantDetailsDialogClose = () => {
    setDialogOpen(false);
    setPlantRowData([]);
    setSelectedUser({ name: "", email: "" });
    removeLocalStorageItem(USER_MANAGEMENT_SELECTED_USER);
  };

  useEffect(() => {
    const fetchAccessRequests = async () => {
      setShowOverLayLoader(true);
      try {
        const response = await getAllUsers(
          currentPage,
          pageSize,
          currentFilters,
          sortField,
          sortOrder,
        );

        if (response && response.data) {
          const total = response.data.total_users || 0;
          const users = response.data.users || [];

          setRowData(users);
          setTotalUsers(total);
        } else {
          setRowData([]);
          setTotalUsers(0);
        }
      } catch (error) {
        setRowData([]);
        setTotalUsers(0);
        showErrorNotification(t("notificationStrings.somethingWrong"));
      } finally {
        setShowOverLayLoader(false);
      }
    };

    if (token) fetchAccessRequests();
  }, [currentPage, pageSize, currentFilters, sortField, sortOrder, token]);

  useEffect(() => {
    const persistedState = getLocalStorageItem(USER_MANAGEMENT_SELECTED_USER);
    if (!persistedState) return;

    removeLocalStorageItem(USER_MANAGEMENT_SELECTED_USER);

    try {
      const parsedState = JSON.parse(persistedState);
      if (!parsedState?.email) return;

      handleShowPlants({
        data: {
          username: parsedState.name || "",
          email: parsedState.email,
        },
      });
    } catch {
      // If persisted payload is malformed, ignore and continue.
    }
  }, []);

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const response = await exportExcel(UserExcelStrings.exportType);
      if (response && response.data) {
        downloadBlobData(
          response.data,
          t("adminPage.usersFileName"),
          response.headers,
        );
        showSuccessNotification(
          t("notificationStrings.tableDataExportSuccess"),
        );
      }
    } catch (error) {
      showErrorNotification(t("notificationStrings.tableDataExportFailed"));
    } finally {
      setExportLoading(false);
    }
  };

  const onSortChanged = (event) => {
    const sortModel = event.columnApi
      .getAllColumns()
      .map((column) => {
        return {
          colId: column.getColId(),
          sort: column.getSort(),
        };
      })
      .filter((col) => col.sort);

    if (sortModel && sortModel.length > 0) {
      const { colId, sort } = sortModel[0];
      setSortField(colId);
      setSortOrder(sort);
    } else {
      setSortField("created_at");
      setSortOrder("desc");
    }
  };

  const onRequestStatusFilterChange = (filterQuery) => {
    const filters = filterQuery ? filterQuery.split(",") : [];
    setCurrentPage(1);
    setCurrentFilters(filters);
  };

  const handlePageChange = (page, newPageSize) => {
    if (newPageSize && newPageSize !== pageSize) {
      setCurrentPage(1);
      setPageSize(newPageSize);
      return;
    }
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const colDefs = [
    {
      headerName: t("adminPage.userid"),
      field: "id",
      sortable: false,
      flex: 1,
      minWidth: 80,
    },
    {
      headerName: t("adminPage.userName"),
      field: "username",
      sortable: false,
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => emptyValueFormatter(params),
    },
    {
      headerName: t("adminPage.country"),
      field: "country",
      sortable: false,
      flex: 1,
      minWidth: 100,
      valueFormatter: (params) => emptyValueFormatter(params),
    },
    {
      headerName: t("adminPage.company"),
      field: "company",
      sortable: false,
      flex: 1,
      minWidth: 100,
      valueFormatter: (params) => emptyValueFormatter(params),
    },
    {
      headerName: t("adminPage.email"),
      field: "email",
      sortable: false,
      flex: 2.5,
      minWidth: 220,
    },
    {
      headerName: t("adminPage.created_at"),
      field: "created_at",
      sortable: true,
      sortingOrder: ["desc", "asc"],
      sort: sortOrder,
      valueFormatter: (params) => {
        return formatToLocalDateTime(params.value);
      },
      flex: 1.5,
      minWidth: 170,
    },
    {
      headerName: t("adminPage.role"),
      field: "user_role",
      sortable: false,
      flex: 1,
      minWidth: 100,
    },
    {
      headerName: t("adminPage.lastLogin"),
      field: "last_login",
      sortable: false,
      valueFormatter: (params) => {
        return formatToLocalDateTime(params.value);
      },
      flex: 1.5,
      minWidth: 170,
    },
    {
      headerName: t("adminPage.requestStatus"),
      field: "request_status",
      sortable: false,
      flex: 1.5,
      headerComponent: RequestStatusFilterHeader,
      headerComponentParams: {
        onFilterChange: onRequestStatusFilterChange,
        currentFilters: currentFilters,
      },
      valueGetter: (params) =>
        getRequestStatusText(t, params.data.request_status),
      minWidth: 160,
    },
    {
      headerName: t("adminPage.plantCount"),
      field: "plant_count",
      sortable: false,
      lockPinned: true,
      pinned: "right",
      cellRenderer: PlantsCountRenderer,
      flex: 2.2,
      minWidth: 200,
    },
  ];

  return (
    <div className="container-main">
      {showOverLayLoader && (
        <div className="circular-loader-overlay">
          <CircularLoader />
        </div>
      )}
      <Header />

      <div className="main-container">
        <div className="content-container">
          <div className="admin-title">
            <span>{t("adminPage.userDetails")}</span>
            <WPButton onClick={handleExportExcel} disabled={exportLoading}>
              {exportLoading
                ? t("adminPage.exporting")
                : t("adminPage.exportExcel")}
            </WPButton>
          </div>
          <div className="descriptionText marginBtm10">
            {t("adminPage.createdAtInfoText")}
          </div>

          <div className="ro-ag-table">
            <WPAgGrid
              columnDefs={colDefs}
              rowData={rowData}
              tableHeight="95vh"
              onSortChanged={onSortChanged}
              context={{ handleShowPlants }}
            />
            <WPPagination
              totalItems={totalUsers}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />

            {dialogOpen && (
              <CustomModal
                header={
                  selectedUser.name
                    ? `${t("adminPage.plantDetails")} - ${selectedUser.name}`
                    : t("adminPage.plantDetails")
                }
                headerHelper={selectedUser.email}
                profileInitials={
                  selectedUser.name?.charAt(0)?.toUpperCase() ?? ""
                }
                onModalClose={handlePlantDetailsDialogClose}
                width="850px"
                hideFooter
                isLoading={showOverLayLoader}
              >
                <div className="ro-ag-table">
                  <WPAgGrid
                    columnDefs={plantColDefs}
                    rowData={plantRowData}
                    tableHeight="55vh"
                    context={{ handleViewPlant }}
                  />
                </div>
              </CustomModal>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default UserPage;
